import React from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { getMobileReasonData } from "./MobileReasons";

const MobileReasonContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 1.5rem 1rem;
  background: #140b3c;
  margin: 0 1rem;
  max-width: calc(100% - 2rem);
  transform: skewX(-3deg);
  position: relative;
  z-index: 3;
  gap: 1rem;

  box-shadow:
    4px 4px 0px #00ff00,
    8px 8px 0px rgba(0, 255, 0, 0.6),
    12px 12px 0px rgba(0, 255, 0, 0.3);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(0, 255, 0, 0.1) 0%,
      transparent 50%
    );
    border-radius: inherit;
    z-index: -1;
  }
`;

const MobileReasonContent = styled(motion.div)`
  max-width: 100%;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas:
    "title number"
    "description description";
  gap: 0.5rem;
  transform: skewX(3deg);
`;

const NumberContainer = styled.div`
  grid-area: number;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-end;
  gap: 0.25rem;
`;

const NoText = styled.div`
  font-size: 0.7rem;
  font-weight: 900;
  line-height: 1.75;
  font-family: "Inter", sans-serif;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.5;
  white-space: nowrap;

  @media (max-width: 480px) {
    font-size: 0.65rem;
  }

  @media (max-width: 360px) {
    font-size: 0.6rem;
  }
`;

const MobileReasonNumber = styled(motion.div)`
  font-size: 2.2rem;
  font-weight: 700;
  font-family: "Chakra Petch", sans-serif;
  color: #ffffff;
  text-shadow:
    1px 1px #00ff00,
    0px 1px #00ff00;

  white-space: nowrap;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  position: relative;
  opacity: 0.5;
  line-height: 1;

  @media (max-width: 480px) {
    font-size: 1.8rem;
  }

  @media (max-width: 360px) {
    font-size: 1.5rem;
  }
`;

const MobileReasonTitle = styled(motion.h1)`
  grid-area: title;
  font-size: 1.2rem;
  font-weight: 900;
  font-family: "Inter", sans-serif;
  font-style: italic;
  margin: 0;
  color: white;
  line-height: 1.2;
  text-shadow: 1px 0.5px #00ff00;
  text-align: left;

  @media (max-width: 480px) {
    font-size: 1.2rem;
  }

  @media (max-width: 360px) {
    font-size: 1.4rem;
  }
`;

const MobileReasonDescription = styled(motion.p)`
  grid-area: description;
  font-size: 0.85rem;
  line-height: 1.4;
  margin: 0;
  color: rgba(255, 255, 255, 0.9);
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-style: italic;
  text-align: left;

  @media (max-width: 480px) {
    font-size: 0.8rem;
    line-height: 1.3;
  }

  @media (max-width: 360px) {
    font-size: 0.75rem;
    line-height: 1.2;
  }
`;

const MobileReasonDisplay = ({ reasonNumber }) => {
  if (reasonNumber === 0) {
    return (
      <MobileReasonContainer>
        <AnimatePresence mode="wait">
          <MobileReasonContent
            key={0}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.15 }}
          >
            <MobileReasonTitle>
              42 Reasons To Download The Plum App
            </MobileReasonTitle>
          </MobileReasonContent>
        </AnimatePresence>
      </MobileReasonContainer>
    );
  }

  const reason = getMobileReasonData(reasonNumber);
  if (!reason) return null;

  const hasDescription = reason.description && reason.description.trim() !== "";
  const formattedNumber = reasonNumber.toString().padStart(2, "0");

  return (
    <MobileReasonContainer>
      <AnimatePresence mode="wait">
        <MobileReasonContent
          key={reasonNumber}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.15 }}
        >
          <MobileReasonTitle>{reason.title}</MobileReasonTitle>
          <NumberContainer>
            <NoText>No.</NoText>
            <MobileReasonNumber>{formattedNumber}</MobileReasonNumber>
          </NumberContainer>
          {hasDescription && (
            <MobileReasonDescription>
              {reason.description}
            </MobileReasonDescription>
          )}
        </MobileReasonContent>
      </AnimatePresence>
    </MobileReasonContainer>
  );
};

export default MobileReasonDisplay;
